import React from 'react';
import {Box, CircularProgress, Typography} from '@mui/material';
import {coverageScoreColor} from '../coverageScoreColor';

/**
 * High-level coverage ring.
 * - number: scored %
 * - computing: spinner in the ring (no stale %)
 * - label (e.g. "—"): never computed / N/A
 */
const CoverageScoreRing = ({score, size = 120, strokeWidth = 5, label, computing = false}) => {
    if (computing) {
        return (
            <Box
                sx={{
                    position: 'relative',
                    width: size,
                    height: size,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    border: `${strokeWidth}px solid`,
                    borderColor: 'grey.300',
                }}
                aria-busy="true"
                aria-label="Computing coverage"
            >
                <CircularProgress size={Math.max(18, Math.round(size * 0.32))} thickness={4}/>
            </Box>
        );
    }

    if (score == null && label) {
        return (
            <Box
                sx={{
                    position: 'relative',
                    width: size,
                    height: size,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    border: `${strokeWidth}px solid`,
                    borderColor: 'grey.300',
                }}
            >
                <Typography
                    variant="body2"
                    fontFamily="Roboto Mono, monospace"
                    fontWeight={700}
                    color="text.secondary"
                    sx={{fontSize: size <= 72 ? '0.9rem' : '1.05rem'}}
                >
                    {label}
                </Typography>
            </Box>
        );
    }
    if (score == null) {
        return null;
    }
    const pct = Math.min(100, Math.max(0, Number(score)));
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (pct / 100) * circumference;
    const color = coverageScoreColor(pct);

    return (
        <Box
            sx={{
                position: 'relative',
                width: size,
                height: size,
                flexShrink: 0,
            }}
        >
            <svg width={size} height={size} aria-hidden="true">
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={strokeWidth}
                    style={{color: 'var(--mui-palette-grey-300, #e0e0e0)'}}
                />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={color}
                    strokeWidth={strokeWidth}
                    strokeLinecap="butt"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    transform={`rotate(-90 ${size / 2} ${size / 2})`}
                />
            </svg>
            <Typography
                variant="h5"
                fontFamily="Roboto Mono, monospace"
                fontWeight={700}
                sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1,
                    zIndex: 1,
                    pointerEvents: 'none',
                    color,
                    fontSize: size <= 88
                        ? '1.45rem'
                        : `${(size / 120) * 1.8}rem`,
                }}
            >
                {Math.round(pct)}%
            </Typography>
        </Box>
    );
};

export default CoverageScoreRing;
